import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { toggleSpinner, updateMessages } from 'apps/auth/actions/appAction';
import { addExtras, setExtra, updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, removeFieldError, setFieldError, setNewFields, setNewForm } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { HintButton, HintContainer } from 'apps/auth/components/form-multi/styles';
import { getField } from 'apps/auth/components/form-multi/utils';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import {
    CATEGORY_TRANSFER_FROM_CREDIT_CARD_112,
    CATEGORY_TRANSFER_FROM_DEBIT_CARD_111,
    INVALID_CARD_ERROR,
    PAYMENT_CREDIT_BANK_CARD_112,
    PAYMENT_DEBIT_BANK_CARD_111,
    SEND_TO,
} from 'apps/auth/constants/Constants';
import { SHOW_CARD_ERROR_POPUP } from 'apps/auth/constants/ExtraNames';
import { DELIVERY_METHOD, FROM_PRV_ID, PAYMENT_METHOD, SOURCE, SOURCE_CODE_FROM } from 'apps/auth/constants/fieldsName';
import { AUTH_SEND_CHANGE_PAYMENT_METHOD, URL_IMAGES_PAYSYSTEMS } from 'apps/auth/constants/UrlConstants';
import {
    ERR_CARD_PARTNER_IS_DOWN,
    ERR_COUNTRY_NOT_SUPPORTED,
    ERR_PAYMENT_METHOD_TO_COUNTRY_NOT_SUPPORTED,
    ERR_PREPAID_CARD_NOT_SUPPORTED,
    ERR_REJECT_ALL_AFT_DETECTED,
    ERR_REJECT_ALL_OCT_DETECTED,
    ERR_TYPE_OF_CARD_NOT_SUPPORTED,
} from 'apps/auth/constants/WellKnownErrorCodes';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { getBankAlias, timer, toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, useForm, usePageMessages, useTypeOfTransfer } from 'apps/auth/hooks/selectors';
import { usePrevious } from 'apps/auth/hooks/usePrevious';
import { useReducerCardFiled } from 'apps/auth/hooks/useReducer';
import { arrowButtonClick, CardService, onBlurHandler, onFocusHandler, isExpiryValid } from 'apps/auth/services/CardService';
import { getError } from 'apps/auth/services/sendToFieldsService';

import NewCardCvvField from './NewCardCvvField';
import NewCardExpiredField from './NewCardExpiredField';
import NewPanField from './NewPanField';
import { BankLogoContainer, CardContainer, CardCvv, CardExpiredDate, CardPanField, CardRow, LogoPaysystem } from './styles';
import TextPopup from '../sharedComponents/popups/TextPopup';

const useControlFocus = (panValue, expDateValue, isShowExpDate, isShowCvv, cvv = '') => {
    useEffect(() => {
        const isExpDateHasFullValue = /\d{2}\/\d{2}/.test(expDateValue);
        if (isShowExpDate && panValue?.length > 12 && !isExpDateHasFullValue) {
            document.getElementById('inputMaskCardExp').focus();
        }
        if (isShowCvv && isExpDateHasFullValue && cvv?.length < 3) {
            document.getElementById('inputCardCvv').focus();
        }
    }, [isShowExpDate, isShowCvv, panValue, expDateValue]);
};

const NewCardField = ({
    isNotValidatePanOnBlur = false,
    isClearErrorByTyping = true,
    actionType = SEND_TO,
    paySysName = '',
    bankLogo = null,
    binExtraName = 'binInfo',
    redirectByBinInfo = false,
    isGlobalFlow = false,
    isBonusWithdraw = false,
    ...props
}) => {
    const popupRef = useRef(null);
    const popupFullwidthRef = useRef(null);
    const modalRef = useRef(null);
    const { fields } = useForm(props.formName);
    const { showRedirectByBinPopup, showCardErrorPopUp, countryIsNotSupported, redirectUrl, cardTypeError, availablePaymentMethodsList, binInfo, toCountry } =
        useExtra();
    const {
        redirectSupportedCountryCountryName,
        redirectSupportedCountryCurrencyCode,
        redirectUnsupportedCountryCountryName,
        redirectUnsupportedCountryCurrencyCode,
        addOtherCard,
        addUsaCardComment,
        addUsaCardCommentLong,
        gotIt,
        buttonCardsAddOther,
        addInsteadAnotherType,
        continueWithThisCard,
        cardTypeText,
        dcSourceExpired,
        expiredLabel,
    } = usePageMessages();
    const [state, dispatch] = useReducerCardFiled(props.expiredDate, props.cvv, props.pan);
    const timerId = useRef(null);
    const controller = useRef(null);
    const cardService = useRef(null);
    const panInputRef = useRef(null);
    useControlFocus(props.pan?.value, props.expiredDate?.value, state.isShowExpDate, state.isShowCvv, props.cvv?.value);
    const prevPanValue = usePrevious(props.pan?.value);
    const typeOfTransfer = useTypeOfTransfer();

    useEffect(() => {
        cardService.current = new CardService(
            props.formName,
            binExtraName,
            props.redirectByBinUrl,
            props.countryIdForCard,
            actionType,
            props.countryA2To,
            props.countryA2From,
            props.invalidCardError,
        );
        cardService.current.setFormAction(props.removeFieldError, props.setFieldError, props.setNewForm, props.setNewFields);
        cardService.current.setExtraAction(props.updateExtraByName, props.addExtras, props.setExtra);
        cardService.current.setAppAction(props.toggleSpinner, props.updateMessages);
        cardService.current.setMessageActions(props.updatePageMessages);
        cardService.current.setRedirectByBinInfo(redirectByBinInfo);
        cardService.current.isGlobalFlow(isGlobalFlow);
        cardService.current.isBonusWithdraw(isBonusWithdraw);
        cardService.current.setTypeOfTransfer(typeOfTransfer);
        cardService.current.visibleFields(props.visibleFields);
    }, [props.countryA2To]);

    useEffect(() => {
        const panValue = props.pan?.value;
        if (panValue && panValue?.length > 12 && prevPanValue !== panValue) {
            timerId.current = timer(
                timerId.current,
                () => {
                    if (isGlobalFlow) {
                        cardService.current.getCardInfoForGlobalProvider(panValue, props.pan, props.fields, controller, popupRef, popupFullwidthRef, modalRef);
                    } else {
                        cardService.current.checkPaymentService(panValue, props.pan, props.fields, controller, binInfo);
                    }
                },
                500,
            );
        }
        if (panValue?.length < 6) {
            clearBinWarnings();
        }
    }, [props.pan?.value]);

    useEffect(() => {
        if (showRedirectByBinPopup) {
            const text = countryIsNotSupported
                ? cardService.current.popupText(redirectUnsupportedCountryCountryName, redirectUnsupportedCountryCurrencyCode)
                : cardService.current.popupText(redirectSupportedCountryCountryName, redirectSupportedCountryCurrencyCode);
            const icon = countryIsNotSupported ? 'plus' : 'check';
            const buttonText = countryIsNotSupported ? addOtherCard : gotIt;
            let clearPan = true;
            popupRef.current?.open(
                <SimplePopup
                    text={text}
                    popupRef={popupRef}
                    onPopupClose={() => {
                        if (clearPan) {
                            props.changeFormFieldValue(props.formName, null, props.pan?.name);
                        }
                    }}
                >
                    <PopupButton
                        iconName={icon}
                        text={buttonText}
                        onClick={(e) => {
                            e.preventDefault();
                            if (countryIsNotSupported) {
                                popupRef.current?.close();
                            } else {
                                clearPan = false;
                                cardService.current.redirectByBin(redirectUrl, fields, popupRef, popupFullwidthRef, modalRef);
                            }
                        }}
                    />
                </SimplePopup>,
            );
            props.setDisabledSubmitButton(true);
            props.updateExtraByName('showRedirectByBinPopup', false);
            props.updateExtraByName('countryIsNotSupported', false);
        }
    }, [showRedirectByBinPopup]);

    useEffect(() => {
        if (cardTypeError) {
            const switchCardType = (e) => {
                e.preventDefault();
                popupRef.current.close();
                props.toggleSpinner(true);
                const codeFrom =
                    availablePaymentMethodsList[props.isCreditCard ? CATEGORY_TRANSFER_FROM_CREDIT_CARD_112 : CATEGORY_TRANSFER_FROM_DEBIT_CARD_111]?.code;
                const fields = {
                    ...props.fields,
                    [SOURCE_CODE_FROM]: getField(SOURCE_CODE_FROM, codeFrom),
                    [SOURCE]: getField(SOURCE, codeFrom),
                    [FROM_PRV_ID]: '',
                    [PAYMENT_METHOD]: getField(PAYMENT_METHOD, props.isCreditCard ? PAYMENT_CREDIT_BANK_CARD_112 : PAYMENT_DEBIT_BANK_CARD_111),
                };
                apiFetchData(
                    `${AUTH_SEND_CHANGE_PAYMENT_METHOD}${toLowerCase(props.toCountryCode)}/${toLowerCase(props.toCurrencyCode)}/${fields?.[DELIVERY_METHOD]?.value}?type=${typeOfTransfer}`,
                    fields,
                )
                    .then((json) => {
                        if (json.messageBodyMessages) {
                            props.updateMessages(json.messageBodyMessages);
                        }
                        props.updatePageMessages(json.messages);
                        if (json?.extra) {
                            props.addExtras(json.extra);
                        }

                        const form = json.forms[props.formName];
                        if (form?.fields) {
                            props.setNewFields(props.formName, form?.fields);
                        }
                    })
                    .finally(() => props.toggleSpinner(false))
                    .catch((error) => {
                        logWarn(error);
                    });
            };
            popupRef.current?.open(
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={cardTypeText} />} popupRef={popupRef} handleClose={clear} onOverlayClick={clear}>
                    <PopupButton iconName="arrow-forward" text={continueWithThisCard} onClick={switchCardType} />
                    <PopupButton iconName="plus" text={addInsteadAnotherType} onClick={clear} />
                </SimplePopup>,
            );
            props.updateExtraByName('cardTypeError', false);
        }
    }, [cardTypeError]);

    const clear = (e) => {
        e?.preventDefault();
        props.changeFormFieldValue(props.formName, null, props.pan?.name);
        props.removeFieldError(props.formName, props.pan?.name);
        panInputRef.current?.focus();
        popupRef.current?.close();
    };

    const clearBinWarnings = () => {
        if (binInfo?.warningAllowed) {
            props.updateExtraByName('binInfo', { ...binInfo, warningAllowed: false });
        }
    };

    useEffect(() => {
        const text = binInfo?.error;
        if (showCardErrorPopUp) {
            if (binInfo?.warningAllowed || binInfo?.warningRestricted) {
                popupRef.current?.open(
                    <SimplePopup
                        text={<DangerouslyHtml dangerouslyHtml={text} />}
                        popupRef={popupRef}
                        handleClose={(e) => {
                            clear(e);
                            clearBinWarnings();
                        }}
                        onOverlayClick={(e) => {
                            clear(e);
                            clearBinWarnings();
                        }}
                    >
                        <PopupButton
                            iconName="plus"
                            text={buttonCardsAddOther}
                            onClick={(e) => {
                                clear(e);
                                clearBinWarnings();
                            }}
                        />
                        {binInfo?.warningAllowed && (
                            <PopupButton
                                iconName="check"
                                text={gotIt}
                                onClick={() => {
                                    popupRef.current?.close();
                                    panInputRef.current?.focus();
                                    props.removeFieldError(props.formName, props.pan?.name);
                                    props.updateExtraByName('binInfo', { ...binInfo, error: null });
                                }}
                            />
                        )}
                    </SimplePopup>,
                );
            } else {
                popupRef.current?.open(
                    <SimplePopup text={<DangerouslyHtml dangerouslyHtml={text} />} popupRef={popupRef} handleClose={clear} onOverlayClick={clear}>
                        <PopupButton iconName="plus" text={buttonCardsAddOther} onClick={clear} />
                    </SimplePopup>,
                );
            }
            props.updateExtraByName(SHOW_CARD_ERROR_POPUP, false);
        }
    }, [showCardErrorPopUp]);

    const onBlurHandlerPanField = (value, field) => {
        if (!field.isError) {
            cardService.current.checkMod10(value, props.pan, props.invalidCardError);
        }
        onBlurHandler(dispatch, props.expiredDate, props.cvv, state, props.pan?.isError);
    };

    const onChangeHandlerPanField = () => {
        if (isClearErrorByTyping) {
            props.updateExtraByName(binExtraName, null);
            props.removeFieldError(props.formName, props.pan?.name);
        }
    };

    const isShowErrorHintButton = (errorCode) =>
        errorCode === ERR_PREPAID_CARD_NOT_SUPPORTED ||
        errorCode === ERR_REJECT_ALL_OCT_DETECTED ||
        errorCode === ERR_REJECT_ALL_AFT_DETECTED ||
        errorCode === ERR_COUNTRY_NOT_SUPPORTED ||
        errorCode === ERR_PAYMENT_METHOD_TO_COUNTRY_NOT_SUPPORTED ||
        errorCode === ERR_CARD_PARTNER_IS_DOWN ||
        errorCode === ERR_TYPE_OF_CARD_NOT_SUPPORTED;

    const isShowExpDateError = props.expiredDate?.isError && state.isShowExpDate && !props.pan?.isError;
    const isShowCvvDateError = props.cvv?.isError && state.isShowCvv && !props.pan?.isError && !props.expiredDate?.isError;
    const isShowArrowButton = props.pan?.value?.length > 15 && !props.pan?.isError && !state.isShowShortValue && (!!props.expiredDate || !!props.cvv);
    const shortValuePanField = { ...props.pan, value: props.pan?.value?.slice(-4) ?? '' };

    const handlePaste = () => {
        clearBinWarnings();
    };

    return (
        <>
            <CardContainer>
                <div>
                    <CardRow>
                        <BankLogoContainer>{getBankAlias(bankLogo, 'card')}</BankLogoContainer>
                        {props.pan?.value && <Text ellipsis>{props.bankName}</Text>}
                    </CardRow>
                </div>
                <div>
                    <CardRow>
                        <CardPanField>
                            <NewPanField
                                formName={props.formName}
                                field={state.isShowShortValue ? shortValuePanField : props.pan}
                                pan={props.pan}
                                panInputRef={panInputRef}
                                isNotValidateOnBlur={isNotValidatePanOnBlur}
                                onBlurHandler={onBlurHandlerPanField}
                                onChangeHandler={onChangeHandlerPanField}
                                onFocus={() => onFocusHandler(dispatch, props.expiredDate, props.cvv, state)}
                                onPaste={handlePaste}
                                arrowButtonClick={(e) => arrowButtonClick(e, dispatch, state, props.expiredDate, props.cvv)}
                                isFocused={state.isFocused}
                                isNewInput
                                isLinedInput
                                maskForInput={state.isShowShortValue && '•• 9999'}
                                hasArrowButton={isShowArrowButton}
                                binInfoError={binInfo?.warningAllowed || binInfo?.warningRestricted ? binInfo?.error : props.binInfoError}
                                toCountry={toCountry}
                            />
                        </CardPanField>
                        {state.isShowExpDate && (
                            <CardExpiredDate>
                                <NewCardExpiredField
                                    field={props.expiredDate}
                                    formName={props.formName}
                                    isNewInput
                                    isLinedInput
                                    isClearErrorOnFocus
                                    setFieldError={props.setFieldError}
                                    errorMessage={dcSourceExpired || expiredLabel}
                                    isExpiryValid={isExpiryValid}
                                />
                            </CardExpiredDate>
                        )}
                        {state.isShowCvv && (
                            <CardCvv>
                                <NewCardCvvField
                                    field={props.cvv}
                                    formName={props.formName}
                                    isClearErrorOnFocus={true}
                                    forwardedRef={props.cvvForwardedRef}
                                    isNewInput
                                    isLinedInput
                                    isDetached
                                />
                            </CardCvv>
                        )}
                    </CardRow>
                    <CardRow $betweenJustify>
                        <div />
                        <LogoPaysystem>
                            {props.pan?.value && paySysName?.length > 0 && paySysName !== 'other' && (
                                <img src={`${URL_IMAGES_PAYSYSTEMS}/${paySysName}.svg`} alt={paySysName} />
                            )}
                        </LogoPaysystem>
                    </CardRow>
                </div>
            </CardContainer>
            {props.pan?.isError && (
                <HintContainer>
                    <Text variant="paragraph" color="error" size="secondary">
                        {getError(props.pan, props.messages)}
                        {isShowErrorHintButton(props.pan.errorCode) && (
                            <HintButton>
                                <DiskButton
                                    size="small"
                                    type="button"
                                    variant="errorHint"
                                    iconName="info-rounded"
                                    iconColor="error"
                                    raised={false}
                                    secondaryButtonClick={() => {
                                        if (props.showPaymentMethodScreen) {
                                            props.showPaymentMethodScreen();
                                        }
                                    }}
                                    onClick={() => {
                                        if (props.hintButtonClick) {
                                            props.hintButtonClick(props.pan, props.showPaymentMethodScreen);
                                        }
                                    }}
                                />
                            </HintButton>
                        )}
                    </Text>
                </HintContainer>
            )}
            {props.canShowUsaCardComment && addUsaCardComment && (
                <HintContainer isLinedInput={props.isLinedInput}>
                    <Text variant="paragraph" color="secondary" size="secondary">
                        {addUsaCardComment}
                    </Text>
                    {addUsaCardCommentLong && (
                        <HintButton>
                            <DiskButton
                                size="small"
                                type="button"
                                variant="tertiary"
                                iconName="info-rounded"
                                iconColor="secondary"
                                raised={false}
                                onClick={() => popupRef.current?.open(<TextPopup text={addUsaCardCommentLong} buttonText={gotIt} popupRef={popupRef} />)}
                            />
                        </HintButton>
                    )}
                </HintContainer>
            )}
            {isShowExpDateError && (
                <HintContainer>
                    <Text color="error" size="secondary" variant="paragraph">
                        {getError(props.expiredDate, props.messages)}
                    </Text>
                </HintContainer>
            )}
            {isShowCvvDateError && (
                <HintContainer>
                    <Text color="error" size="secondary" variant="paragraph">
                        {getError(props.cvv, props.messages)}
                    </Text>
                </HintContainer>
            )}
            {(!addUsaCardComment || (!props.canShowUsaCardComment && addUsaCardComment)) && props.pan?.allowedCardTypes && (
                <HintContainer>
                    <Text color="secondary" size="secondary" variant="paragraph">
                        {props.pan?.allowedCardTypes}
                    </Text>
                </HintContainer>
            )}
            <PopupNew ref={popupRef} />
            <PopupNew ref={popupFullwidthRef} fullwidthPopup />
            <ModalNew ref={modalRef} />
        </>
    );
};
NewCardField.propTypes = {
    isNotValidatePanOnBlur: PropTypes.bool,
    isClearErrorByTyping: PropTypes.bool,
    actionType: PropTypes.string,
    cardBankName: PropTypes.string,
    paySysName: PropTypes.string,
    bankLogo: PropTypes.string,
    binExtraName: PropTypes.string,
    panName: PropTypes.string,
    binInfoError: PropTypes.string,
    expiredDateName: PropTypes.string,
    cvvName: PropTypes.string,
    redirectByBinInfo: PropTypes.bool,
    isGlobalFlow: PropTypes.bool,
    hintButtonClick: PropTypes.func,
};
const mapStateToProps = ({ form, extra, messages }, ownProps) => {
    const binExtraName = ownProps.binExtraName ?? 'binInfo';
    return {
        fields: form[ownProps.formName].fields,
        pan: form[ownProps.formName].fields[ownProps.panName],
        expiredDate: form[ownProps.formName].fields[ownProps.expiredDateName],
        cvv: form[ownProps.formName].fields[ownProps.cvvName],
        bankName: extra[binExtraName]?.bankName,
        bankLogo: extra[binExtraName]?.bankAlias,
        paySysName: extra[binExtraName]?.paysysName,
        binInfoError: extra[binExtraName]?.error,
        isCreditCard: extra[binExtraName]?.creditCard,
        messages,
        invalidCardError: messages.jsMessages[INVALID_CARD_ERROR],
        toCountryCode: extra.toCountry?.code,
        toCurrencyCode: extra.toCurrency?.code.toLowerCase(),
    };
};
export default connect(mapStateToProps, {
    removeFieldError,
    changeFormFieldValue,
    setFieldError,
    setNewForm,
    setNewFields,
    updateExtraByName,
    addExtras,
    setExtra,
    toggleSpinner,
    updateMessages,
    updatePageMessages,
})(NewCardField);
